import { useEffect, useState } from "react";
import { toast } from "sonner";
import { affiliatePortalApi } from "../lib/affiliateApi";

/**
 * AffiliateContractGateModal — espelha a lógica do TermsGateModal.js, mas no
 * portal do afiliado: ao montar (toda vez que o afiliado loga), busca a versão
 * vigente do Contrato de Afiliados e compara com
 * affiliate.accepted_contract_version. Se diferente (contrato mudou desde o
 * último aceite, ou afiliado antigo nunca aceitou), bloqueia as métricas e
 * exige o aceite da versão atual antes de continuar.
 *
 * Não renderiza nada se não houver contrato publicado ou se a versão aceita já
 * for a atual — self-contained, monta uma vez por sessão logada.
 */
const AffiliateContractGateModal = () => {
    const [contract, setContract] = useState(null); // { content, version }
    const [needsAccept, setNeedsAccept] = useState(false);
    const [accepting, setAccepting] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [contractRes, me] = await Promise.all([
                    affiliatePortalApi.contract().catch(() => null),
                    affiliatePortalApi.me().catch(() => null),
                ]);
                if (!alive) return;
                if (!contractRes || !contractRes.version || !contractRes.content) return; // nada publicado ainda
                if (!me || !me.id) return; // só bloqueia afiliado logado
                setContract(contractRes);
                if (me.accepted_contract_version !== contractRes.version) {
                    setNeedsAccept(true);
                }
            } catch (_) { /* silencioso — nunca trava o portal por causa disso */ }
        })();
        return () => { alive = false; };
    }, []);

    if (!needsAccept || !contract) return null;

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await affiliatePortalApi.acceptContract();
            setNeedsAccept(false);
            toast.success("Contrato aceito", { description: `Versão ${contract.version} registrada.` });
        } catch (e) {
            toast.error("Falha ao registrar aceite", { description: e?.message || "Tente novamente." });
        } finally {
            setAccepting(false);
        }
    };

    return (
        <div
            data-testid="affiliate-contract-gate-modal"
            className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div className="w-full max-w-lg rounded-3xl border border-amber-500/20 bg-slate-950 shadow-2xl overflow-hidden">
                <div className="p-6 border-b border-white/5">
                    <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center mb-4">
                        <i className="fa-solid fa-handshake text-xl"></i>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">
                        Atualização do Contrato de Afiliados
                    </div>
                    <h2 className="salon-heading text-2xl text-white mt-1">
                        Nosso Contrato de Afiliados mudou
                    </h2>
                    <p className="text-xs text-slate-500 mt-1">
                        Para continuar acessando o portal, leia e aceite a versão {contract.version}.
                    </p>
                </div>
                <div className="p-6 max-h-[45vh] overflow-y-auto text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
                    {contract.content}
                </div>
                <div className="p-6 border-t border-white/5">
                    <button
                        data-testid="affiliate-contract-gate-accept-btn"
                        onClick={handleAccept}
                        disabled={accepting}
                        className="w-full min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold uppercase tracking-[0.15em] text-sm transition-all disabled:opacity-60"
                    >
                        {accepting ? (
                            <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Registrando…</>
                        ) : (
                            "Aceitar e Continuar"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AffiliateContractGateModal;
